import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function WhyChooseUs() {
  const { t } = useTranslation('common');
  const [clickedCountry, setClickedCountry] = useState<number | null>(null);

  const reasons = [
    {
      icon: 'ri-shopping-bag-3-line',
      title: t('why_reason1_title'),
      description: t('why_reason1_desc'),
      color: 'from-green-600 to-green-700'
    },
    {
      icon: 'ri-price-tag-3-line',
      title: t('why_reason2_title'),
      description: t('why_reason2_desc'),
      color: 'from-orange-600 to-orange-700'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: t('why_reason3_title'),
      description: t('why_reason3_desc'),
      color: 'from-cyan-600 to-cyan-700'
    },
    {
      icon: 'ri-box-3-line',
      title: t('why_reason4_title'),
      description: t('why_reason4_desc'),
      color: 'from-red-600 to-red-700'
    }
  ];

  const countries = [
    { name: t('why_country_mexico'), flag: '🇲🇽', color: 'from-green-400 via-white to-red-400' },
    { name: t('why_country_honduras'), flag: '🇭🇳', color: 'from-blue-400 via-white to-blue-400' },
    { name: t('why_country_salvador'), flag: '🇸🇻', color: 'from-blue-400 via-white to-blue-400' },
    { name: t('why_country_nicaragua'), flag: '🇳🇮', color: 'from-blue-400 via-white to-blue-400' }
  ];

  const handleCountryClick = (index: number) => {
    setClickedCountry(index);
    setTimeout(() => setClickedCountry(null), 1000);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-green-100 text-green-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            {t('why_badge')}
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {t('why_title')} <span className="text-green-600">{t('why_title_highlight')}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('why_subtitle')}
          </p>
        </div>

        {/* Reasons Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {reasons.map((reason, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-100"
            >
              <div className="flex items-start gap-6">
                {/* Icon */}
                <div className={`w-16 h-16 flex items-center justify-center bg-gradient-to-br ${reason.color} rounded-xl flex-shrink-0 shadow-lg`}>
                  <i className={`${reason.icon} text-white text-2xl`}></i>
                </div>

                {/* Content */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {reason.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {reason.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-16 bg-gradient-to-r from-red-50 via-orange-50 to-yellow-50 rounded-2xl p-12 text-center">
          <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
            {t('why_additional_title')}
          </h3>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-8">
            {t('why_additional_subtitle')}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6">
            {countries.map((country, index) => (
              <button
                key={index}
                onClick={() => handleCountryClick(index)}
                className={`relative bg-white px-8 py-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-110 cursor-pointer group overflow-hidden ${
                  clickedCountry === index ? 'animate-celebration' : ''
                }`}
              >
                {/* Animated Background on Click */}
                {clickedCountry === index && (
                  <>
                    <div className={`absolute inset-0 bg-gradient-to-r ${country.color} animate-pulse-fast opacity-80`}></div>
                    <div className="absolute inset-0 animate-confetti-1">
                      <div className="absolute top-0 left-1/4 w-2 h-2 bg-red-500 rounded-full"></div>
                      <div className="absolute top-0 left-3/4 w-2 h-2 bg-yellow-500 rounded-full"></div>
                    </div>
                    <div className="absolute inset-0 animate-confetti-2">
                      <div className="absolute top-0 left-1/3 w-2 h-2 bg-green-500 rounded-full"></div>
                      <div className="absolute top-0 left-2/3 w-2 h-2 bg-blue-500 rounded-full"></div>
                    </div>
                    <div className="absolute inset-0 animate-confetti-3">
                      <div className="absolute top-0 left-1/2 w-2 h-2 bg-orange-500 rounded-full"></div>
                      <div className="absolute top-0 left-1/6 w-2 h-2 bg-pink-500 rounded-full"></div>
                    </div>
                  </>
                )}
                
                {/* Gradient Border Effect on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-r ${country.color} opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-2xl`}></div>
                
                <div className="relative z-10 flex flex-col items-center gap-3">
                  <span className="text-7xl transform group-hover:scale-125 group-hover:rotate-12 transition-all duration-300 filter drop-shadow-lg">
                    {country.flag}
                  </span>
                  <span className="text-gray-800 font-bold text-xl group-hover:text-gray-900 transition-colors whitespace-nowrap">
                    {country.name}
                  </span>
                </div>

                {/* Shine Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes celebration {
          0%, 100% { transform: scale(1) rotate(0deg); }
          25% { transform: scale(1.2) rotate(-5deg); }
          50% { transform: scale(1.3) rotate(5deg); }
          75% { transform: scale(1.2) rotate(-5deg); }
        }

        @keyframes pulse-fast {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 0.4; }
        }

        @keyframes confetti-1 {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100px) rotate(180deg); opacity: 0; }
        }

        @keyframes confetti-2 {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(120px) rotate(-180deg); opacity: 0; }
        }

        @keyframes confetti-3 {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110px) rotate(90deg); opacity: 0; }
        }

        .animate-celebration {
          animation: celebration 1s ease-in-out;
        }

        .animate-pulse-fast {
          animation: pulse-fast 0.5s ease-in-out infinite;
        }

        .animate-confetti-1 {
          animation: confetti-1 1s ease-out forwards;
        }

        .animate-confetti-2 {
          animation: confetti-2 1s ease-out forwards;
          animation-delay: 0.1s;
        }

        .animate-confetti-3 {
          animation: confetti-3 1s ease-out forwards;
          animation-delay: 0.2s;
        }
      `}</style>
    </section>
  );
}
